import { createRouter, createWebHashHistory } from "vue-router"
import LoginView from "../views/LoginView.vue"
import { useAuthStore } from "@/stores/auth"

const BASE =
  (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.BASE_URL) ||
  (typeof process !== "undefined" && process.env && process.env.BASE_URL) ||
  "/"

const routes = [
  { path: "/", redirect: "/login" },

  { path: "/login", name: "Login", component: LoginView },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("../views/ForgotPasswordView.vue"),
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("../views/ResetPasswordView.vue"),
  },

  {
    path: "/shop",
    name: "ProductList",
    component: () => import("../views/ClientView/ProductList.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK", "PRACOWNIK", "KIOSK"] }
  },
  {
    path: "/shop/:shoperId",
    component: () => import("../views/ClientView/ProductDetails.vue"),
    name: "ProductDetails",
    props: true,
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK", "PRACOWNIK", "KIOSK"] }
  },
  {
    path: '/favorites',
    name: 'Favorites',
    component: () => import('@/views/ClientView/FavoritesList.vue'),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK", "PRACOWNIK", "KIOSK"] }
  },
  {
    path: "/cart",
    name: "Cart",
    component: () => import("../views/ClientView/CartView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK", "PRACOWNIK"] },
  },
  {
    path: "/orders",
    redirect: "/orders/pending",
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK"] }
  },
  {
    path: "/orders/pending",
    name: "PendingQueueView",
    component: () => import("../views/AdminPanel/Orders/PendingQueueView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK"] }
  },
  {
    path: "/orders/placed",
    name: "PlacedOrdersView",
    component: () => import("../views/AdminPanel/Orders/PlacedOrdersView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK"] }
  },
  {
    path: "/admin_carts",
    name: "AdminActiveCarts",
    component: () => import("../views/AdminActiveCarts.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK"] }
  },
  {
    path: "/products/client",
    name: "ClientProducts",
    component: () => import("../views/AdminPanel/Products/ClientProductsTable.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK"] }
  },
  {
    path: "/settings/client",
    name: "AppearanceSettingsView",
    component: () => import("../views/AdminPanel/ShopSettings/AppearanceSettingsView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK"] }
  },
  {
    path: "/settings/company",
    name: "Client",
    component: () => import("../views/AdminPanel/ShopSettings/CompanySettingsView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK"] }
  },
  {
    path: "/settings/employes",
    name: "Employes",
    component: () => import("../views/AdminPanel/ShopSettings/EmployesView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN", "PARTNER", "KIEROWNIK"] }
  },
  {
    path: "/settings/users",
    name: "Users",
    component: () => import("../views/AdminPanel/AdminSettings/UsersView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN"] }
  },
  {
    path: "/settings/shops",
    name: "Shops",
    component: () => import("../views/AdminPanel/AdminSettings/ShopsView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN"] }
  },
  {
    path: "/settings/integrations",
    name: "Integrations",
    component: () => import("../views/AdminPanel/AdminSettings/IntegrationsView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN"] },
  },
  {
    path: "/settings/integrationsXml",
    name: "IntegrationsXml",
    component: () => import("../views/AdminPanel/AdminSettings/IntegrationsXmlView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN"] },
  },
  {
    path: "/settings/shippings",
    name: "AdminShippingSettingsView",
    component: () => import("../views/AdminPanel/AdminSettings/AdminShippingSettingsView.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN"] },
  },
  {
    path: "/admin/catalog-settings",
    name: "AdminCatalogSettings",
    component: () => import("../views/AdminPanel/AdminSettings/AdminCatalogSettings.vue"),
    meta: { requiresAuth: true, roles: ["ADMIN"] },
  }
]

const router = createRouter({
  history: createWebHashHistory(BASE),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  const publicPaths = ['/login', '/forgot-password', '/reset-password']
  if (publicPaths.includes(to.path)) {
    if (to.path === '/login') {
      await auth.bootstrap()
      if (auth.isAuthenticated) return '/shop'
    }
    return true
  }

  const needsAuth = !!to.meta?.requiresAuth
  if (!needsAuth) return true

  await auth.bootstrap()
  if (!auth.isAuthenticated) return '/login'

  const roles = Array.isArray(to.meta?.roles) ? to.meta.roles : null
  if (roles && roles.length) {
    const ok = auth.hasAnyRole(roles)
    if (!ok) return '/shop'
  }

  return true
})

export default router